import React from 'react';
import { Card, CardContent, Typography, Box, Button } from '@mui/material';
import { usePermissionCheck } from '../../../core/hooks/usePermissionCheck';
import { useAuth } from '../../auth/context/AuthContext';
import { apiService } from 'core/services/apiService';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import UpdateEmail from './UpdateEmail';
import UpdatePassword from './UpdatePassword';

export const SecuritySettings = () => {
	const { user } = useAuth();
	const { hasEditPermission } = usePermissionCheck();
	const { showToast } = useToastContext();
	const canEditSecurity = hasEditPermission('Security');
	const [isUpdatingEmail, setIsUpdatingEmail] = React.useState(false);
	const [isUpdatingPassword, setIsUpdatingPassword] = React.useState(false);
	const [isLoadingEmail, setIsLoadingEmail] = React.useState(false);
	const [isLoadingPassword, setIsLoadingPassword] = React.useState(false);

	const onCancelUpdateEmail = () => {
		setIsUpdatingEmail(false);
	};

	const onCancelUpdatePassword = () => {
		setIsUpdatingPassword(false);
	};

	const onSubmitUpdateEmail = async (newEmail: string) => {
		if (!canEditSecurity) return;
		setIsLoadingEmail(true);
		try {
			const tenant = localStorage.getItem('BYOD-organizationId');
			if (!tenant) {
				throw new Error('Organization ID not found');
			}

			// Convert email to lowercase before sending to API (with null check)
			await apiService.put('/users/security-settings/email', {
				tenant,
				currentEmail: user.email ? user.email.toLowerCase() : '',
				newEmail: newEmail ? newEmail.toLowerCase() : ''
			});

			// Update the local user state with new email
			const normalizedEmail = newEmail ? newEmail.toLowerCase() : '';
			
			// Update localStorage to persist the change
			const storedData = localStorage.getItem('BYOD-app');
			if (storedData) {
				const parsedData = JSON.parse(storedData);
				if (parsedData.user) {
					parsedData.user.email = normalizedEmail;
					localStorage.setItem('BYOD-app', JSON.stringify(parsedData));
				}
			}
			
			// Update the user object (this will trigger a re-render)
			user.email = normalizedEmail;
			
			showToast('success', 'Email updated successfully');
			setIsUpdatingEmail(false);
		} catch (error: any) {
			console.error('Failed to update email:', error);
			const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update email. Please try again.';
			showToast('error', errorMessage);
		} finally {
			setIsLoadingEmail(false);
		}
	};

	const onSubmitUpdatePassword = async (newPassword: string) => {
		if (!canEditSecurity) return;
		setIsLoadingPassword(true);
		try {
			const tenant = localStorage.getItem('BYOD-organizationId');
			if (!tenant) {
				throw new Error('Organization ID not found');
			}

			// Convert email to lowercase before sending to API (with null check)
			await apiService.put('/users/security-settings/password', {
				email: user.email ? user.email.toLowerCase() : '',
				tenant,
				password: newPassword
			});

			showToast('success', 'Password updated successfully');
			setIsUpdatingPassword(false);
		} catch (error: any) {
			console.error('Failed to update password:', error);
			const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update password. Please try again.';
			showToast('error', errorMessage);
		} finally {
			setIsLoadingPassword(false);
		}
	};

	if (isUpdatingEmail && canEditSecurity) {
		return (
			<UpdateEmail
				existingEmail={user.email}
				onCancel={onCancelUpdateEmail}
				onSubmit={onSubmitUpdateEmail}
				isLoading={isLoadingEmail}
			/>
		);
	}
	if (isUpdatingPassword && canEditSecurity) {
		return (
			<UpdatePassword 
				onCancel={onCancelUpdatePassword} 
				onSubmit={onSubmitUpdatePassword}
				isLoading={isLoadingPassword}
			/>
		);
	}
	return (
		<Card>
			<CardContent>
				<Typography variant="h5" gutterBottom sx={{ fontFamily: 'Gotham' }}>
					Security - {user?.email}
				</Typography>

				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Box>
						<Typography fontWeight="bold" sx={{ fontFamily: 'Gotham' }}>Email</Typography>
						<Typography sx={{ fontFamily: 'Gotham' }}>{user?.email || 'No email provided'}</Typography>
					</Box>

					<Button
						variant="contained"
						sx={{
							backgroundColor: '#446388',
							marginTop: '3%',
							// fontSize: '0.7rem',
							opacity: canEditSecurity ? 1 : 0.6
						}}
						disabled={!canEditSecurity}
						onClick={() => setIsUpdatingEmail(true)}
					>
						Edit
					</Button>
				</Box>

				<Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
					<Box>
						<Typography fontWeight="bold" sx={{ fontFamily: 'Gotham' }}>Password</Typography>
						<Typography sx={{ fontFamily: 'Gotham' }}>Click the button to change your password</Typography>
					</Box>
					<Button
						variant="contained"
						sx={{
							backgroundColor: '#446388',
							marginTop: '3%',
							// fontSize: '0.7rem',
							opacity: canEditSecurity ? 1 : 0.6
						}}
						disabled={!canEditSecurity}
						onClick={() => setIsUpdatingPassword(true)}
					>
						Edit
					</Button>
				</Box>
			</CardContent>
		</Card>
	);
};
